import React, { createContext, useContext, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from './AuthContext';

const DatabaseContext = createContext();

export function DatabaseProvider({ children }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Teams functions
  const getTeams = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('teams')
        .select('*')
        .order('name');
      
      if (error) throw error;
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const createTeam = async (teamData) => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('teams')
        .insert([{ ...teamData, user_id: user.id }])
        .select();
      
      if (error) throw error;
      return data[0];
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateTeam = async (teamId, teamData) => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('teams')
        .update(teamData)
        .eq('id', teamId)
        .select();
      
      if (error) throw error;
      return data[0];
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteTeam = async (teamId) => {
    try {
      setLoading(true);
      setError(null);

      const { error } = await supabase
        .from('teams')
        .delete()
        .eq('id', teamId);
      
      if (error) throw error;
      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Matches functions
  const getMatches = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('matches')
        .select(`
          *,
          home_team:teams!home_team_id(id, name, color),
          away_team:teams!away_team_id(id, name, color)
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getMatchById = async (matchId) => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('matches')
        .select(`
          *,
          home_team:teams!home_team_id(id, name, color),
          away_team:teams!away_team_id(id, name, color)
        `)
        .eq('id', matchId)
        .single();
      
      if (error) throw error;
      
      // Get events for this match
      const { data: events, error: eventsError } = await supabase
        .from('events')
        .select('*')
        .eq('match_id', matchId)
        .order('seconds', { ascending: true });
      
      if (eventsError) throw eventsError;
      
      return { ...data, events: events || [] };
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const createMatch = async (matchData) => {
    try {
      setLoading(true);
      setError(null);

      // Check if user is available - important for authentication
      if (!user) {
        throw new Error('User not authenticated. Please log in to save matches to the database.');
      }

      // Create the match
      const { data, error } = await supabase
        .from('matches')
        .insert([{ 
          name: matchData.matchName || 'Rugby Match',
          home_team_name: matchData.homeTeamName,
          away_team_name: matchData.awayTeamName,
          home_team_id: matchData.homeTeam?.id, // Optional chaining in case homeTeam object is not provided
          away_team_id: matchData.awayTeam?.id, // Optional chaining in case awayTeam object is not provided
          home_team_score: matchData.homeTeamScore || 0,
          away_team_score: matchData.awayTeamScore || 0,
          user_id: user.id
        }])
        .select();
      
      if (error) throw error;
      
      // If there are events, add them
      if (matchData.events && matchData.events.length > 0) {
        const eventsToInsert = matchData.events.map(event => ({
          match_id: data[0].id,
          event_type: event.eventType,
          team: event.team,
          player_number: event.playerNumber,
          timestamp: event.timestamp,
          seconds: event.seconds,
          period: event.period,
          position_x: event.position?.x || 0,
          position_y: event.position?.y || 0,
          comment: event.comment || null,
          zone: event.zone || null
        }));
        
        const { error: eventsError } = await supabase
          .from('events')
          .insert(eventsToInsert);
        
        if (eventsError) throw eventsError;
      }
      
      return data[0];
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateMatch = async (matchId, matchData) => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('matches')
        .update({
          name: matchData.matchName,
          home_team_score: matchData.homeTeam.score || 0,
          away_team_score: matchData.awayTeam.score || 0
        })
        .eq('id', matchId)
        .select();
      
      if (error) throw error;
      return data[0];
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteMatch = async (matchId) => {
    try {
      setLoading(true);
      setError(null);

      // First delete all events associated with this match
      const { error: eventsError } = await supabase
        .from('events')
        .delete()
        .eq('match_id', matchId);
      
      if (eventsError) throw eventsError;
      
      // Then delete the match
      const { error } = await supabase
        .from('matches')
        .delete()
        .eq('id', matchId);
      
      if (error) throw error;
      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Events functions
  const createEvent = async (matchId, eventData) => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('events')
        .insert([{
          match_id: matchId,
          event_type: eventData.eventType,
          team: eventData.team,
          player_number: eventData.playerNumber,
          timestamp: eventData.timestamp,
          seconds: eventData.seconds,
          period: eventData.period,
          position_x: eventData.position?.x || 0,
          position_y: eventData.position?.y || 0,
          comment: eventData.comment || null,
          zone: eventData.zone || null
        }])
        .select();
      
      if (error) throw error;
      return data[0];
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateEvent = async (eventId, eventData) => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('events')
        .update({
          event_type: eventData.eventType,
          team: eventData.team,
          player_number: eventData.playerNumber,
          comment: eventData.comment || null,
          zone: eventData.zone || null
        })
        .eq('id', eventId)
        .select();
      
      if (error) throw error;
      return data[0];
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteEvent = async (eventId) => {
    try {
      setLoading(true);
      setError(null);

      const { error } = await supabase
        .from('events')
        .delete()
        .eq('id', eventId);
      
      if (error) throw error;
      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    loading,
    error,
    // Teams
    getTeams,
    createTeam,
    updateTeam,
    deleteTeam,
    // Matches
    getMatches,
    getMatchById,
    createMatch,
    updateMatch,
    deleteMatch,
    // Events
    createEvent,
    updateEvent,
    deleteEvent
  };

  return <DatabaseContext.Provider value={value}>{children}</DatabaseContext.Provider>;
}

export const useDatabase = () => {
  const context = useContext(DatabaseContext);
  if (!context) {
    throw new Error('useDatabase must be used within a DatabaseProvider');
  }
  return context;
};